/**
 * useFamilyInviteDetails Hook
 *
 * Fetches family plan invitation details using invite code.
 * Extracted from family-accept.tsx (Line Count Refactor 2025-11-21)
 *
 * Features:
 * - React Query caching and stale-while-revalidate
 * - Invite validation (status, expiration)
 * - Admin name resolution
 * - Analytics tracking (Screen_View_FamilyAccept)
 *
 * Phase: 1.6 - Family Plans
 * Refactored: 2025-11-21 (family-accept.tsx: 421 → 250 lines)
 *
 * DTO contract (2026-04-18, Wave 4B):
 * ─────────────────────────────────────────────────────────
 * Migrated from `authenticatedFetch + manual JSON cast` to
 * `fetchJsonValidated(FamilyInviteDetailsDto, ...)` — server responses are
 * now validated against the shared Zod contract. The underlying
 * `get_family_invite_details(p_invite_code)` RPC (SECURITY DEFINER) is
 * unchanged; only the client parse layer gained validation.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '../../../lib/analytics/events';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { FamilyInviteDetailsDto } from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';
import type { InviteDetails } from '../lib/family/familyInviteTypes';

/**
 * Query result type for invite details fetch
 */
interface InviteDetailsResult {
  inviteDetails: InviteDetails | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch family plan invitation details
 *
 * Uses the `get_family_invite_details` SECURITY DEFINER RPC to perform
 * cross-user lookups (invite by code + admin profile) in a single call.
 * Works for both Supabase Auth and Better Auth users (anon key is sufficient).
 *
 * Validates invite status and expiration client-side after fetching.
 * Tracks Screen_View_FamilyAccept analytics event after successful load.
 *
 * @param code - Invitation code from URL (e.g., 'ABC123')
 * @returns Query result with invite details, loading state, and error
 *
 * @example
 * ```tsx
 * function FamilyAcceptScreen() {
 *   const { code } = useLocalSearchParams<{ code: string }>();
 *   const { inviteDetails, isLoading, error } = useFamilyInviteDetails(code);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage message={error} />;
 *   if (!inviteDetails) return <InvalidCodeMessage />;
 *
 *   return <InvitationDetails invite={inviteDetails} />;
 * }
 * ```
 */
export function useFamilyInviteDetails(code: string | undefined): InviteDetailsResult {
  const { t } = useTranslation('subscription');

  const {
    data: inviteDetails,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['family-invite', code],
    queryFn: async (): Promise<InviteDetails | null> => {
      if (!code) {
        throw new Error(t('family.acceptInvite.invalidCode'));
      }

      let dto;
      try {
        dto = await fetchJsonValidated(
          FamilyInviteDetailsDto,
          getEdgeFunctionUrl('get-family-invite') + '?code=' + encodeURIComponent(code),
        );
      } catch {
        throw new Error(t('family.acceptInvite.invalidCode'));
      }

      // Check invite status
      if (dto.status !== 'pending') {
        throw new Error(
          t('family.acceptInvite.statusProcessed', { status: dto.status })
        );
      }

      // Check expiration
      if (new Date(dto.expires_at) < new Date()) {
        throw new Error(t('family.acceptInvite.invalidCode'));
      }

      // Map camelCase-safe DTO to the existing snake_case-oriented InviteDetails
      // interface consumed by the screen. The DTO is already snake_case on the
      // wire; the mapping is essentially an identity with status re-typed.
      const details: InviteDetails = {
        id: dto.id,
        family_plan_id: dto.family_plan_id,
        invited_email: dto.invited_email,
        status: dto.status,
        created_at: dto.created_at,
        expires_at: dto.expires_at,
        family_plan: {
          tier: dto.family_plan.tier,
          billing_interval: dto.family_plan.billing_interval,
          daily_quota: dto.family_plan.daily_quota,
          admin_user_id: dto.family_plan.admin_user_id,
          admin_name: dto.family_plan.admin_name,
          member_count: dto.family_plan.member_count,
        },
      };
      return details;
    },
    enabled: !!code,
    staleTime: 1000 * 60 * 5, // 5 minutes (invites don't change frequently)
    retry: false, // Don't retry on error (invalid codes should fail immediately)
  });

  // Track screen view after invite details loaded
  useEffect(() => {
    if (inviteDetails && code) {
      trackEvent('Screen_View_FamilyAccept', {
        invite_code: code.substring(0, 4), // Obfuscated (first 4 chars)
        tier: inviteDetails.family_plan.tier,
      });
    }
  }, [inviteDetails, code]);

  return {
    inviteDetails: inviteDetails || null,
    isLoading,
    error: queryError ? (queryError as Error).message : null,
    refetch,
  };
}
